import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import {
  parseStableApiError,
  StableApiErrorResponse,
} from "../product-revival/contracts";

// ═══════════════════════════════════════════════════════════════════════════
// SHARED API CLIENT FACTORY
// Creates a configured Axios instance with token refresh, error formatting,
// and auth interceptors. Used by web, desktop, and admin apps.
// ═══════════════════════════════════════════════════════════════════════════

export interface ApiClientConfig {
  /** Raw API URL (may or may not end with /api/v1) */
  rawUrl: string;
  /** Callback to get current access token */
  getAccessToken: () => string | null;
  /** Callback to get current refresh token */
  getRefreshToken: () => string | null;
  /** Callback to store new tokens after refresh (legacy synchronous fallback). */
  setTokens: (
    accessToken: string,
    refreshToken: string,
  ) => void | Promise<void>;
  /**
   * Monotonic session generation. Login/logout/session replacement must change
   * this value so a late refresh response cannot revive an older session.
   */
  getSessionGeneration?: () => string | number;
  /**
   * Atomically commit a refresh only when the captured session still owns it.
   * Returning false discards a stale response without logging out the new
   * session.
   */
  commitRefreshedSession?: (
    response: SessionRefreshResponse,
    context: SessionRefreshContext,
  ) => boolean | Promise<boolean>;
  /** Persistent, non-secret idempotency nonce used to recover a lost refresh response. */
  getRefreshRecoveryNonce?: () => string | null;
  /** Persist the recovery nonce before the refresh request is sent. */
  setRefreshRecoveryNonce?: (nonce: string) => void | Promise<void>;
  /** Clear only the nonce that completed or was definitively rejected. */
  clearRefreshRecoveryNonce?: (expectedNonce: string) => void | Promise<void>;
  /** Optional callback with the complete refreshed login/session response. */
  onSessionRefreshed?: (response: unknown) => void;
  /** Callback when auth fails completely (refresh failed) */
  onAuthFailure: () => void;
  /** Optional: Callback to get current permission version */
  getPermVersion?: () => string | number | null;
  /** Optional: Callback when permissions are stale (409 response) */
  onPermStale?: () => void;
  /** Stable release application identifier attached to every request. */
  clientApplication?: "desktop" | "web" | "mobile" | "admin";
  /** Semver-compatible client version used by the mutation compatibility gate. */
  clientVersion?: string;
  /** Stable per-installation source device identifier for command audit. */
  sourceDevice?: string;
  /** Optional legal business date supplied by the active store/session. */
  getBusinessDate?: () => string | null | undefined;
  /** Test seam for the isolated refresh request. */
  refreshRequest?: (request: RefreshRequest) => Promise<SessionRefreshResponse>;
}

export interface SessionRefreshResponse {
  accessToken: string;
  refreshToken: string;
  user?: unknown;
  [key: string]: unknown;
}

export interface SessionRefreshContext {
  accessToken: string | null;
  refreshToken: string;
  sessionGeneration: string | number | null;
  recoveryNonce: string;
}

export interface RefreshRequest {
  url: string;
  refreshToken: string;
  recoveryNonce: string;
  headers: Record<string, string>;
}

export interface ActiveStoreSessionResponse<
  TUser extends { storeId?: string },
> {
  accessToken: string;
  refreshToken?: string | null;
  user: TUser;
}

export interface ResolvedActiveStoreSession<
  TUser extends { storeId?: string },
> {
  accessToken: string;
  refreshToken: string;
  user: TUser;
  storeId: string;
}

/**
 * Validate the server-selected store and retain the device refresh token when
 * active-store intentionally returns null (it only rotates access scope).
 */
export function resolveActiveStoreSession<TUser extends { storeId?: string }>(
  response: ActiveStoreSessionResponse<TUser>,
  requestedStoreId: string,
  currentRefreshToken: string | null,
): ResolvedActiveStoreSession<TUser> {
  if (!response.accessToken) {
    throw new Error("Store switch response did not include an access token");
  }
  if (response.user.storeId !== requestedStoreId) {
    throw new Error("Store switch response did not match the requested store");
  }

  const refreshToken = response.refreshToken || currentRefreshToken;
  if (!refreshToken) {
    throw new Error("Store switch cannot continue without a device session");
  }

  return {
    accessToken: response.accessToken,
    refreshToken,
    user: response.user,
    storeId: requestedStoreId,
  };
}

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export interface SessionAwareAxiosInstance extends AxiosInstance {
  /** Recover a previously sent refresh whose response may have been lost. */
  recoverPendingSession: () => Promise<boolean>;
  /** Serialize a server session mutation with refresh rotation/commit. */
  runSessionMutation: <T>(operation: () => Promise<T>) => Promise<T>;
}

class StaleSessionRefreshError extends Error {
  constructor() {
    super("Refresh response belongs to a stale session");
    this.name = "StaleSessionRefreshError";
  }
}

class MissingRefreshTokenError extends Error {
  constructor() {
    super("No refresh token available");
    this.name = "MissingRefreshTokenError";
  }
}

export class DefinitiveSessionCommitError extends Error {
  readonly definitiveAuthFailure = true;

  constructor(message = "Refreshed session could not be stored securely") {
    super(message);
    this.name = "DefinitiveSessionCommitError";
  }
}

function refreshStatus(error: unknown): number | undefined {
  return (error as AxiosError | undefined)?.response?.status;
}

function isDefinitiveRefreshRejection(error: unknown): boolean {
  if (error instanceof MissingRefreshTokenError) return true;
  if (
    (error as { definitiveAuthFailure?: boolean } | undefined)
      ?.definitiveAuthFailure
  ) {
    return true;
  }
  const status = refreshStatus(error);
  return status === 400 || status === 401 || status === 403;
}

function isDefinitiveSessionCommitFailure(error: unknown): boolean {
  return (
    (error as { definitiveAuthFailure?: boolean } | undefined)
      ?.definitiveAuthFailure === true
  );
}

export type StableAxiosError = AxiosError & {
  stableError?: StableApiErrorResponse;
};

export function readStableAxiosError(
  error: unknown,
): StableApiErrorResponse | undefined {
  const candidate = error as StableAxiosError;
  return (
    candidate?.stableError ?? parseStableApiError(candidate?.response?.data)
  );
}

/**
 * Normalize a raw API URL to ensure it ends with /api/v1
 */
export function normalizeApiUrl(rawUrl: string): string {
  const cleaned = rawUrl.replace(/\/$/, "");
  if (cleaned.endsWith("/api/v1")) return cleaned;
  return `${cleaned.replace(/\/api\/?$/, "")}/api/v1`;
}

export function buildApiClientHeaders(
  config: Pick<
    ApiClientConfig,
    "clientApplication" | "clientVersion" | "sourceDevice" | "getBusinessDate"
  >,
): Record<string, string> {
  const businessDate = config.getBusinessDate?.() ?? undefined;
  return {
    "Content-Type": "application/json",
    ...(config.clientApplication
      ? { "X-Client-App": config.clientApplication }
      : {}),
    ...(config.clientVersion
      ? { "X-Client-Version": config.clientVersion }
      : {}),
    ...(businessDate ? { "X-Business-Date": businessDate } : {}),
    ...(config.sourceDevice ? { "X-Source-Device": config.sourceDevice } : {}),
  };
}

/**
 * Create a configured Axios API client with:
 * - Auto-attached auth tokens
 * - Token refresh with request queuing (prevents race conditions)
 * - Error message formatting (array errors → joined strings)
 */
export function createApiClient(
  config: ApiClientConfig,
): SessionAwareAxiosInstance {
  const baseURL = normalizeApiUrl(config.rawUrl);

  const client = axios.create({
    baseURL,
    headers: buildApiClientHeaders(config),
    timeout: 30000, // 30s timeout — fail fast when offline instead of hanging
  });

  const currentSessionMatches = (context: SessionRefreshContext) => {
    if (config.getAccessToken() !== context.accessToken) return false;
    if (config.getRefreshToken() !== context.refreshToken) return false;
    if (config.getSessionGeneration && context.sessionGeneration !== null) {
      return config.getSessionGeneration() === context.sessionGeneration;
    }
    return true;
  };

  const commitRefresh = async (
    response: SessionRefreshResponse,
    context: SessionRefreshContext,
  ): Promise<boolean> => {
    if (!currentSessionMatches(context)) return false;
    if (config.commitRefreshedSession) {
      return config.commitRefreshedSession(response, context);
    }
    await config.setTokens(response.accessToken, response.refreshToken);
    return true;
  };

  const clearRecoveryNonce = async (nonce: string) => {
    await config.clearRefreshRecoveryNonce?.(nonce);
  };

  const refreshSession = async (
    refreshToken: string,
    recoveryNonce: string,
  ): Promise<SessionRefreshResponse> => {
    const headers = {
      ...buildApiClientHeaders(config),
      "Idempotency-Key": recoveryNonce,
    };
    if (config.refreshRequest) {
      return config.refreshRequest({
        url: `${baseURL}/auth/refresh`,
        refreshToken,
        recoveryNonce,
        headers,
      });
    }
    const response = await axios.post<SessionRefreshResponse>(
      `${baseURL}/auth/refresh`,
      { refreshToken },
      { headers, timeout: 30000 },
    );
    return response.data;
  };

  const performSessionRefresh = async (): Promise<SessionRefreshResponse> => {
    let refreshContext: SessionRefreshContext | null = null;
    try {
      const refreshToken = config.getRefreshToken();
      if (!refreshToken) {
        throw new MissingRefreshTokenError();
      }

      const sessionGeneration = config.getSessionGeneration?.() ?? null;
      const recoveryNonce =
        config.getRefreshRecoveryNonce?.() || crypto.randomUUID();
      refreshContext = {
        accessToken: config.getAccessToken(),
        refreshToken,
        sessionGeneration,
        recoveryNonce,
      };
      await config.setRefreshRecoveryNonce?.(recoveryNonce);
      if (!currentSessionMatches(refreshContext)) {
        throw new StaleSessionRefreshError();
      }

      const response = await refreshSession(refreshToken, recoveryNonce);
      if (!response.accessToken || !response.refreshToken) {
        throw new MissingRefreshTokenError();
      }
      const committed = await commitRefresh(response, refreshContext);
      if (!committed) {
        throw new StaleSessionRefreshError();
      }

      await clearRecoveryNonce(recoveryNonce);
      config.onSessionRefreshed?.(response);
      return response;
    } catch (refreshError) {
      if (
        refreshContext &&
        !currentSessionMatches(refreshContext) &&
        !isDefinitiveSessionCommitFailure(refreshError)
      ) {
        await clearRecoveryNonce(refreshContext.recoveryNonce);
        throw refreshError instanceof StaleSessionRefreshError
          ? refreshError
          : new StaleSessionRefreshError();
      }
      if (isDefinitiveRefreshRejection(refreshError)) {
        if (refreshContext) {
          await clearRecoveryNonce(refreshContext.recoveryNonce);
        }
        config.onAuthFailure();
      }
      throw refreshError;
    }
  };

  let activeRefreshPromise: Promise<SessionRefreshResponse> | null = null;
  let activeSessionMutation: Promise<unknown> | null = null;
  let sessionMutationTail: Promise<void> = Promise.resolve();

  const startSessionRefresh = (
    allowDuringSessionMutation = false,
  ): Promise<SessionRefreshResponse> => {
    if (activeRefreshPromise) return activeRefreshPromise;
    if (activeSessionMutation && !allowDuringSessionMutation) {
      return activeSessionMutation.then(() => startSessionRefresh());
    }
    const pending = performSessionRefresh();
    activeRefreshPromise = pending;
    void pending.then(
      () => {
        if (activeRefreshPromise === pending) activeRefreshPromise = null;
      },
      () => {
        if (activeRefreshPromise === pending) activeRefreshPromise = null;
      },
    );
    return pending;
  };

  // Request interceptor — attach auth token and permission version
  client.interceptors.request.use(
    (reqConfig) => {
      // Let the browser/Axios add the multipart boundary. Keeping the client's
      // default application/json header causes Multer to receive no file.
      if (
        typeof FormData !== "undefined" &&
        reqConfig.data instanceof FormData
      ) {
        reqConfig.headers.delete("Content-Type");
      }
      const token = config.getAccessToken();
      if (token) {
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      // Attach permission version if available
      if (config.getPermVersion) {
        const permVersion = config.getPermVersion();
        if (permVersion !== null && permVersion !== undefined) {
          reqConfig.headers["X-Perm-Version"] = String(permVersion);
        }
      }
      const businessDate = config.getBusinessDate?.() ?? undefined;
      if (businessDate) {
        reqConfig.headers["X-Business-Date"] = businessDate;
      }
      if (config.sourceDevice) {
        reqConfig.headers["X-Source-Device"] = config.sourceDevice;
      }
      return reqConfig;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor — error formatting + token refresh + perm version check
  client.interceptors.response.use(
    (response) => response,
    async (error: StableAxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;
      const responseData = error.response?.data as
        | Record<string, unknown>
        | undefined;

      // Handle permission version mismatch (409 PERM_STALE)
      if (error.response?.status === 409) {
        const code = (responseData as Record<string, unknown>)?.code;
        if (code === "PERM_STALE") {
          if (config.onPermStale) {
            config.onPermStale();
          }
          // Return the error so client can handle refresh
          return Promise.reject(error);
        }
      }

      // Format error messages from API response
      if (responseData) {
        const stableError = parseStableApiError(responseData);
        if (stableError) error.stableError = stableError;
        if (
          Array.isArray(responseData.errors) &&
          responseData.errors.length > 0
        ) {
          (responseData as Record<string, unknown>).message = (
            responseData.errors as string[]
          ).join("\n");
        } else if (Array.isArray(responseData.message)) {
          (responseData as Record<string, unknown>).message = (
            responseData.message as string[]
          ).join("\n");
        }
      }

      // Skip token refresh only for the login/refresh/logout endpoints themselves
      // (to avoid infinite loops). Other /auth/* endpoints like /auth/validate-session
      // still need a valid access token and should trigger refresh when expired.
      const noRefreshPaths = [
        "/auth/login",
        "/auth/refresh",
        "/auth/logout",
        "/auth/register",
        "/auth/forgot-password",
        "/auth/reset-password",
      ];
      const isAuthEndpoint = noRefreshPaths.some((p) =>
        originalRequest?.url?.startsWith(p),
      );

      // If there's no response at all (network error), don't attempt token
      // refresh — the server is unreachable. This prevents forced logout when
      // the app is simply offline.
      if (!error.response && error.code === "ERR_NETWORK") {
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        !originalRequest?._retry &&
        !isAuthEndpoint
      ) {
        if (originalRequest) {
          originalRequest._retry = true;
        }
        try {
          const response = await startSessionRefresh(
            originalRequest?.url?.startsWith("/auth/active-store") === true,
          );

          if (originalRequest) {
            originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
            return client(originalRequest);
          }
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  const sessionAwareClient = client as SessionAwareAxiosInstance;
  sessionAwareClient.recoverPendingSession = async () => {
    if (!activeRefreshPromise && !config.getRefreshRecoveryNonce?.()) {
      return false;
    }
    await startSessionRefresh();
    return true;
  };
  sessionAwareClient.runSessionMutation = <T>(
    operation: () => Promise<T>,
  ): Promise<T> => {
    const pending = sessionMutationTail.then(async () => {
      if (activeRefreshPromise) await activeRefreshPromise;
      return operation();
    });
    activeSessionMutation = pending;
    sessionMutationTail = pending.then(
      () => undefined,
      () => undefined,
    );
    void pending.then(
      () => {
        if (activeSessionMutation === pending) activeSessionMutation = null;
      },
      () => {
        if (activeSessionMutation === pending) activeSessionMutation = null;
      },
    );
    return pending;
  };

  return sessionAwareClient;
}
